<template>
  <section class="section">
    <box>
      <div slot="header">
        <div class="columns is-vcentered">
          <div class="column">
            {{ $l("components.server.edrServerChild", "EDR Server Child") }}
          </div>
          <div class="column is-narrow">
            <button
              class="button is-primary is-small"
              @click.prevent="showCreatePage"
            >
              <octicon :icon="plus" />&ensp;
              {{ $l("components.policies.add", "Add") }}
            </button>
          </div>
        </div>
      </div>

      <data-table
        ref="table"
        :key="currentLocale"
        :items="serverListFunc"
        :per-page="10"
        :sort-desc="true"
        :theme="$_ui_theme_tables"
      >
        <data-column
          :sortable="false"
          field="serial"
          :label="$l('components.server.serial', 'No')"
          width="7%"
        />

        <data-column
          field="name"
          :label="$l('components.server.name', 'Name')"
          width="13%"
        >
          <template #default="{ item }">
            <div class="truncate">
              <Tooltip :content="item.fingerprint" placement="top">
                <span
                  @dblclick="showServerInfor(item)"
                  style="cursor: pointer"
                  >{{ item.name }}</span
                >
              </Tooltip>
            </div>
          </template>
        </data-column>

        <data-column
          field="ip"
          :label="$l('components.server.ipAddress', 'IP Address')"
          width="13%"
        />

        <data-column
          field="createdAt"
          :label="$l('components.server.createdAt', 'Created At')"
          :render="renderTime"
          width="13%"
        />

        <data-column
          field="lastConnection"
          :label="$l('components.server.lastConnection', 'Last Connection')"
          width="13%"
        >
          <template #default="{ item }">
            <div
              class="status-column"
              :class="{
                'status-inactive':
                  item.lastConnection !== '0001-01-01T00:00:00Z' &&
                  dateDistance(item.lastConnection) <= -3,
                'status-expired':
                  item.lastConnection !== '0001-01-01T00:00:00Z' &&
                  dateDistance(item.lastConnection) <= -1 &&
                  dateDistance(item.lastConnection) > -3,
              }"
            >
              {{ renderTime(item.lastConnection) }}
            </div>
          </template>
        </data-column>

        <data-column
          field="status"
          :label="$l('components.server.status', 'Status')"
          width="13%"
        >
          <template #header>
            <div class="column-header">
              <span>{{
                $l("components.configurationUpdate.status", "Status")
              }}</span>
              <Dropdown
                placement="bottom-start"
                trigger="custom"
                :visible="filter.filterStatus.isFilterOpen"
                @on-clickoutside="
                  handleCloseFilter(filter.filterStatus.filterType)
                "
              >
                <span
                  href="javascript:void(0)"
                  @click="toggleFilter('filterStatus')"
                  class="icon"
                >
                  <svg-icon
                    type="mdi"
                    :path="path"
                    :class="{
                      'filter-active': filter.filterStatus.isSubmitFilter,
                    }"
                  ></svg-icon>
                </span>
                <DropdownMenu slot="list">
                  <div class="menu-list">
                    <label v-for="(i, index) in listStatus" :key="index">
                      <DropdownItem>
                        <input
                          type="checkbox"
                          v-model="filter.filterStatus.filterValues"
                          :value="i.value"
                        />

                        <span style="margin-left: 10px">{{
                          $l(`components.configurationUpdate.${i.name}`)
                        }}</span>
                      </DropdownItem>
                      <div class="divider"></div>
                    </label>

                    <div class="filter-button">
                      <Button
                        type="text"
                        size="small"
                        :disabled="!filter.filterStatus.filterValues.length"
                        @click="submitFilter(filter.filterStatus.filterType)"
                        ><span style="font-weight: bold">{{
                          $l("global.submit", "Submit")
                        }}</span></Button
                      >
                      <Button
                        type="text"
                        size="small"
                        color="#2096f2"
                        @click="resetFilter(filter.filterStatus.filterType)"
                        ><span style="font-weight: bold">{{
                          $l("global.reset", "Reset")
                        }}</span></Button
                      >
                    </div>
                  </div>
                </DropdownMenu>
              </Dropdown>
            </div>
          </template>
          <template #default="{ item }">
            <div
              class="status-column"
              :class="{
                'status-active':
                  item.status && dateDistance(item.expiredAt) >= 7,
                'status-inactive': !item.status,
                'status-expired':
                  dateDistance(item.expiredAt) < 7 && item.status,
              }"
            >
              {{
                item.status
                  ? $l("components.server.enabled", "Enabled")
                  : $l("components.server.disabled", "Disabled")
              }}
            </div>
          </template>
        </data-column>

        <data-column
          field="statistics"
          :label="$l('components.server.statistics', 'Statistics')"
          :sortable="false"
          width="20%"
          :render="renderStatistics"
        />

        <data-column :sortable="false" width="8%">
          <template v-if="isAdmin" #default="{ item }">
            <div class="actions-column-server">
              <Dropdown trigger="click" placement="bottom-start">
                <span href="javascript:void(0)" style="cursor: pointer">
                  <octicon :icon="kebabHorizontal" label="kebabHorizontal" />
                </span>
                <DropdownMenu slot="list">
                  <DropdownItem
                    v-if="item.status"
                    @click.native="showUpdatePage(item)"
                  >
                    <span>{{
                      $l("components.server.update", "Update")
                    }}</span></DropdownItem
                  >
                  <div class="divider" v-if="item.status"></div>

                  <DropdownItem
                    @click.native="changeStatus(item)"
                    v-if="!item.status"
                  >
                    <span style="color: #2096f2">{{
                      $l("components.configurationUpdate.enable", "Enable")
                    }}</span>
                  </DropdownItem>

                  <DropdownItem
                    @click.native="changeStatus(item)"
                    v-if="item.status"
                  >
                    <span style="color: red">{{
                      $l("components.configurationUpdate.disable", "Disable")
                    }}</span>
                  </DropdownItem>
                  <div class="divider"></div>

                  <DropdownItem @click.native="showServerInfor(item)">
                    <span>{{
                      $l("components.server.viewInfor", "View server Infor")
                    }}</span>
                  </DropdownItem>

                  <div class="divider" v-if="item.status"></div>
                  <DropdownItem
                    @click.native="downloadServerFile(item)"
                    v-if="item.status"
                  >
                    <span>{{
                      $l(
                        "components.server.downloadActivationFile",
                        "Download activation file"
                      )
                    }}</span>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </div>
          </template>
        </data-column>
      </data-table>
    </box>

    <modal :show.sync="formModal.formInfor.isFormOpen" content-width="90%">
      <box>
        <div class="close-icon" @click="formModal.formInfor.isFormOpen = false">
          <Icon type="ios-close" size="25" />
        </div>
        <div style="text-align: center; margin-bottom: 20px">
          <strong>{{
            $l("components.server.detailInfor", "Detail information")
          }}</strong>
        </div>

        <div class="columns">
          <div class="column is-4">
            <box style="height: 363px">
              <div class="columns" style="overflow: scroll">
                <div class="column nowrap is-4 mr-5">
                  <strong>{{
                    $l("components.server.name", "Name") + ":"
                  }}</strong>
                  <br />
                  <strong>{{
                    $l("components.server.fingerprint", "Identifier") + ":"
                  }}</strong>
                  <br />
                  <strong>{{
                    $l("components.server.email", "Email") + ":"
                  }}</strong>
                  <br />
                  <strong>{{
                    $l("components.server.phoneNumber", "Phone number") + ":"
                  }}</strong>
                  <br />
                  <strong>{{
                    $l("components.server.address", "Address") + ":"
                  }}</strong>
                  <br />
                  <strong>{{
                    $l("components.server.company", "Company") + ":"
                  }}</strong>
                  <br />
                  <strong>{{
                    $l("components.server.description", "Description") + ":"
                  }}</strong>
                  <br />
                  <strong>{{
                    $l("components.server.ipAddress", "IP address") + ":"
                  }}</strong>
                  <br />
                  <strong>{{
                    $l("components.server.createdAt", "Created at") + ":"
                  }}</strong>
                  <br />
                  <strong>{{
                    $l("components.server.status", "Status") + ":"
                  }}</strong>
                  <br />
                  <strong>{{
                    $l("components.server.agentLimit", "Agent limit") + ":"
                  }}</strong>
                  <br />
                  <strong>{{
                    $l("components.server.expiredAt", "Expired at") + ":"
                  }}</strong>
                  <br />
                  <strong>{{
                    $l("components.server.lastConnection", "Last connection") +
                    ":"
                  }}</strong>
                </div>

                <div class="column nowrap is-8">
                  <span>{{
                    formModal.formInfor.values.name
                      ? formModal.formInfor.values.name
                      : $l("components.settings.empty", "N/A")
                  }}</span>
                  <br />
                  <span>{{
                    formModal.formInfor.values.fingerprint
                      ? formModal.formInfor.values.fingerprint
                      : $l("components.settings.empty", "N/A")
                  }}</span>
                  <br />
                  <span>{{
                    formModal.formInfor.values.email
                      ? formModal.formInfor.values.email
                      : $l("components.settings.empty", "N/A")
                  }}</span>
                  <br />
                  <span>{{
                    formModal.formInfor.values.phone_number
                      ? formModal.formInfor.values.phone_number
                      : $l("components.settings.empty", "N/A")
                  }}</span>
                  <br />
                  <span>{{
                    formModal.formInfor.values.address
                      ? formModal.formInfor.values.address
                      : $l("components.settings.empty", "N/A")
                  }}</span>
                  <br />
                  <span>{{
                    formModal.formInfor.values.company
                      ? formModal.formInfor.values.company
                      : $l("components.settings.empty", "N/A")
                  }}</span>
                  <br />
                  <span>{{
                    formModal.formInfor.values.description
                      ? formModal.formInfor.values.description
                      : $l("components.settings.empty", "N/A")
                  }}</span>
                  <br />
                  <span>{{
                    formModal.formInfor.values.ip
                      ? formModal.formInfor.values.ip
                      : $l("components.settings.empty", "N/A")
                  }}</span>
                  <br />
                  <span>{{
                    formModal.formInfor.values.createdAt
                      ? formModal.formInfor.values.createdAt
                      : $l("components.settings.empty", "N/A")
                  }}</span>
                  <br />
                  <span
                    class="status-column"
                    :class="{
                      'status-active': formModal.formInfor.values.status,
                      'status-inactive': !formModal.formInfor.values.status,
                    }"
                    >{{
                      formModal.formInfor.values.status
                        ? $l("components.server.enabled", "Enabled")
                        : $l("components.server.disabled", "Disabled")
                    }}</span
                  >
                  <br />
                  <span>{{
                    formModal.formInfor.values.agentLimit
                      ? formModal.formInfor.values.agentLimit === -1
                        ? $l("components.server.unlimit", "Unlimit")
                        : formModal.formInfor.values.agentLimit
                      : $l("components.server.empty", "N/A")
                  }}</span>
                  <br />
                  <span>{{
                    renderTime(formModal.formInfor.values.expiredAt)
                  }}</span>
                  <br />
                  <span>{{
                    renderTime(formModal.formInfor.values.lastConnection)
                  }}</span>
                </div>
              </div>
            </box>
          </div>

          <div class="column">
            <box>
              <div class="columns is-gapless">
                <div
                  class="column is-6 statistic"
                  style="
                    color: #27ae60;
                    border: 2px solid #27ae60;
                    margin-right: 10px;
                    padding-right: 0;
                  "
                >
                  <div class="count">
                    {{
                      formModal.formInfor.values.$_statistics &&
                      formModal.formInfor.values.$_statistics.find(
                        (e) => e.type === "agent_count"
                      ) &&
                      formModal.formInfor.values.$_statistics.find(
                        (e) => e.type === "agent_count"
                      ).value
                        ? formModal.formInfor.values.$_statistics.find(
                            (e) => e.type === "agent_count"
                          ).value
                        : 0
                    }}
                  </div>
                  <div class="line" style="background: #27ae60"></div>
                  <div class="text">
                    {{
                      $l(
                        "components.server.agentCount",
                        "The number of agents protected"
                      )
                    }}
                  </div>
                </div>

                <div
                  class="column is-6 statistic"
                  style="
                    color: #56ccf2;
                    border: 2px solid #56ccf2;
                    margin-right: 10px;
                    padding-right: 0;
                  "
                >
                  <div class="count">
                    {{
                      formModal.formInfor.values.$_statistics &&
                      formModal.formInfor.values.$_statistics.find(
                        (e) => e.type === "server_count"
                      ) &&
                      formModal.formInfor.values.$_statistics.find(
                        (e) => e.type === "server_count"
                      ).value
                        ? formModal.formInfor.values.$_statistics.find(
                            (e) => e.type === "server_count"
                          ).value
                        : 0
                    }}
                  </div>
                  <div class="line" style="background: #56ccf2"></div>
                  <div class="text">
                    {{
                      $l(
                        "components.server.serverCount",
                        "The number of managed EDR servers"
                      )
                    }}
                  </div>
                </div>
              </div>

              <div class="columns is-gapless">
                <div
                  class="column is-6 statistic"
                  style="
                    color: #9b51e0;
                    border: 2px solid #9b51e0;
                    margin-right: 10px;
                    padding-right: 0;
                  "
                >
                  <div class="count">
                    {{
                      formModal.formInfor.values.$_statistics &&
                      formModal.formInfor.values.$_statistics.find(
                        (e) => e.type === "critical_threats"
                      ) &&
                      formModal.formInfor.values.$_statistics.find(
                        (e) => e.type === "critical_threats"
                      ).value
                        ? formModal.formInfor.values.$_statistics.find(
                            (e) => e.type === "critical_threats"
                          ).value
                        : 0
                    }}
                  </div>
                  <div class="line" style="background: #9b51e0"></div>
                  <div class="text">
                    {{
                      $l(
                        "components.server.criticalThreats",
                        "Critical Threats"
                      )
                    }}
                  </div>
                </div>

                <div
                  class="column is-6 statistic"
                  style="
                    color: #eb5757;
                    border: 2px solid #eb5757;
                    margin-right: 10px;
                    padding-right: 0;
                  "
                >
                  <div class="count">
                    {{
                      formModal.formInfor.values.$_statistics &&
                      formModal.formInfor.values.$_statistics.find(
                        (e) => e.type === "high_priority_threats"
                      ) &&
                      formModal.formInfor.values.$_statistics.find(
                        (e) => e.type === "high_priority_threats"
                      ).value
                        ? formModal.formInfor.values.$_statistics.find(
                            (e) => e.type === "high_priority_threats"
                          ).value
                        : 0
                    }}
                  </div>
                  <div class="line" style="background: #eb5757"></div>
                  <div class="text">
                    {{
                      $l(
                        "components.server.highPriorityThreats",
                        "High Priority Threats"
                      )
                    }}
                  </div>
                </div>
              </div>

              <div class="columns is-gapless">
                <div
                  class="column is-6 statistic"
                  style="
                    color: #f2994b;
                    border: 2px solid #f2994b;
                    margin-right: 10px;
                    padding-right: 0;
                  "
                >
                  <div class="count">
                    {{
                      formModal.formInfor.values.$_statistics &&
                      formModal.formInfor.values.$_statistics.find(
                        (e) => e.type === "medium_priority_threats"
                      ) &&
                      formModal.formInfor.values.$_statistics.find(
                        (e) => e.type === "medium_priority_threats"
                      ).value
                        ? formModal.formInfor.values.$_statistics.find(
                            (e) => e.type === "medium_priority_threats"
                          ).value
                        : 0
                    }}
                  </div>
                  <div class="line" style="background: #f2994b"></div>
                  <div class="text">
                    {{
                      $l(
                        "components.server.mediumPriorityThreats",
                        "Medium Priority Threats"
                      )
                    }}
                  </div>
                </div>

                <div
                  class="column is-6 statistic"
                  style="
                    color: #2973d6;
                    border: 2px solid #2973d6;
                    margin-right: 10px;
                    padding-right: 0;
                  "
                >
                  <div class="count">
                    {{
                      formModal.formInfor.values.$_statistics &&
                      formModal.formInfor.values.$_statistics.find(
                        (e) => e.type === "low_priority_threats"
                      ) &&
                      formModal.formInfor.values.$_statistics.find(
                        (e) => e.type === "low_priority_threats"
                      ).value
                        ? formModal.formInfor.values.$_statistics.find(
                            (e) => e.type === "low_priority_threats"
                          ).value
                        : 0
                    }}
                  </div>
                  <div class="line" style="background: #2973d6"></div>
                  <div class="text">
                    {{
                      $l(
                        "components.server.lowPriorityThreats",
                        "Low Priority Threats"
                      )
                    }}
                  </div>
                </div>
              </div>
            </box>
          </div>
        </div>

        <div class="columns" v-if="formModal.formInfor.isFormOpen">
          <box>
            <div slot="header">
              <div class="columns is-vcentered">
                <div class="column">
                  {{ $l("components.server.updateHistory", "Update history") }}
                </div>
              </div>
            </div>
            <data-table
              ref="historyTable"
              :key="currentLocale"
              :items="serverHistoryListFunc"
              :per-page="10"
              :sort-desc="true"
              :theme="$_ui_theme_tables"
            >
              <data-column
                :sortable="false"
                field="serial"
                :label="$l('components.server.serial', 'No')"
                width="7%"
              />

              <data-column
                field="name"
                :label="$l('components.server.name', 'Name')"
              >
                <template #default="{ item }">
                  <div class="truncate">
                    {{ item.name }}
                  </div>
                </template>
              </data-column>

              <data-column
                field="createdAt"
                :label="$l('components.server.createdAt', 'Created at')"
                :render="renderTime"
              />

              <data-column
                field="tags"
                :label="$l('components.server.tags', 'Tags')"
                :render="renderTagName"
              />

              <data-column
                field="type"
                :label="$l('components.server.type', 'Type')"
                :render="renderTypeName"
              />

              <data-column
                field="files"
                :label="$l('components.server.numberOfFile', 'File')"
              />

              <data-column
                field="status"
                :label="$l('components.server.status', 'Status')"
              >
                <template #default="{ item }">
                  <div
                    class="status-column"
                    :class="{
                      'status-active': item.status,
                      'status-inactive': !item.status,
                    }"
                  >
                    {{
                      item.status
                        ? $l("components.server.enabled", "Enabled")
                        : $l("components.server.disabled", "Disabled")
                    }}
                  </div>
                </template>
              </data-column>
            </data-table>

            <div slot="footer" class="field is-grouped is-grouped-right">
              <div class="control">
                <button
                  class="button"
                  @click.prevent="formModal.formInfor.isFormOpen = false"
                >
                  {{ $l("global.close", "Close") }}
                </button>
              </div>
            </div>
          </box>
        </div>
      </box>
    </modal>
  </section>
</template>

<script>
import ServerApi from "@/api/Server";
import { mapGetters } from "vuex";
import { plus, kebabHorizontal } from "octicons-vue";
import { DataTable, DataColumn, Modal } from "@cyradar/ui";
import SvgIcon from "@jamescoyle/vue-icon";
import moment from "moment";
import Format from "date-fns/format";
import { mdiFilter } from "@mdi/js";
import {
  Dropdown,
  DropdownMenu,
  DropdownItem,
  Icon,
  Button,
  Tooltip,
} from "iview";
import UpdateApi from "@/api/Update";

export default {
  components: {
    DataTable,
    DataColumn,
    Modal,
    SvgIcon,
    Dropdown,
    DropdownMenu,
    DropdownItem,
    Icon,
    Button,
    Tooltip,
  },

  data() {
    return {
      path: mdiFilter,
      plus,
      kebabHorizontal,

      listStatistics: [],

      listTags: [],

      listTypes: [
        {
          value: "basic_engine",
          name: "Basic Engine",
        },
        {
          value: "installer_online",
          name: "Online Installer",
        },
        {
          value: "installer_offline",
          name: "Offline Installer",
        },
        {
          value: "db_hash",
          name: "Hash Database",
        },
        {
          value: "db_signature",
          name: "Signature Database",
        },
        {
          value: "db_resource",
          name: "Resource Database",
        },
        {
          value: "advance_engine",
          name: "Advance Engine",
        },
      ],

      formModal: {
        formInfor: {
          isFormOpen: false,
          values: {},
          statistics: [],
        },
      },

      filter: {
        filterStatus: {
          filterType: "status",
          isFilterOpen: false,
          isSubmitFilter: false,
          filterValues: [],
        },
      },

      listStatus: [
        {
          value: true,
          name: "enabled",
        },
        {
          value: false,
          name: "disabled",
        },
      ],
    };
  },

  computed: {
    ...mapGetters(["authenticationClaims", "isAdmin", "currentLocale"]),
  },

  methods: {
    dateDistance(date) {
      const now = new Date();
      const endISO = moment(date, moment.ISO_8601);
      const start = moment(now, "HH:mm:ss YYYY-MM-DD");
      const startISO = moment(start.format(), moment.ISO_8601);
      const diff = endISO.diff(startISO, "days");
      return diff;
    },

    renderTime(time) {
      if (time === "0001-01-01T00:00:00Z") {
        return this.$l("components.server.empty", "N/A");
      }
      return Format(time, "HH:mm DD/MM/YYYY");
    },

    renderStatistics(statistics) {
      if (!statistics) {
        return this.$l("components.server.empty", "N/A");
      } else {
        let result = [];
        statistics.map((item) => {
          if (item.type === "agent_count") {
            result.push(`${item.value ? item.value : 0} Agents`);
          }

          if (item.type === "server_count") {
            result.push(`${item.value ? item.value : 0} Servers`);
          }

          if (item.type === "critical_threats") {
            result.push(`${item.value ? item.value : 0} Critical Threats`);
          }

          if (item.type === "high_priority_threats") {
            result.push(`${item.value ? item.value : 0} High Priority Threats`);
          }

          if (item.type === "medium_priority_threats") {
            result.push(
              `${item.value ? item.value : 0} Medium Priority Threats`
            );
          }

          if (item.type === "low_priority_threats") {
            result.push(`${item.value ? item.value : 0} Low Priority Threats`);
          }
        });
        return result.join(", ");
      }
    },

    // Filter

    handleCloseFilter(filterType) {
      if (filterType === this.filter.filterStatus.filterType) {
        this.filter.filterStatus.isFilterOpen = false;
      }
    },

    toggleFilter(type) {
      event.stopPropagation();
      Object.keys(this.filter).forEach((key) => {
        if (key !== type) {
          this.filter[key].isFilterOpen = false;
        }
      });

      this.filter[type].isFilterOpen = !this.filter[type].isFilterOpen;
    },

    resetFilter(filterType) {
      if (filterType === this.filter.filterStatus.filterType) {
        this.filter.filterStatus.isFilterOpen = false;
        this.filter.filterStatus.isSubmitFilter = false;
        this.filter.filterStatus.filterValues = [];
        this.$refs.table.loadItems();
      }
    },

    submitFilter(filterType) {
      if (filterType === this.filter.filterStatus.filterType) {
        this.filter.filterStatus.isFilterOpen = false;
        if (this.filter.filterStatus.filterValues.length === 0) {
          this.filter.filterStatus.isSubmitFilter = false;
        } else {
          this.filter.filterStatus.isSubmitFilter = true;
        }
        this.$refs.table.loadItems();
      }
    },

    // Get server table

    serverListFunc(filtering, sorting, paging) {
      const params = {
        status: this.filter.filterStatus.isSubmitFilter
          ? this.filter.filterStatus.filterValues.join(",")
          : "",
      };

      return ServerApi.getServerVueTable(
        this.$http,
        filtering,
        sorting,
        paging,
        params
      ).then(async (res) => {
        const dataLength = res.data.items.length;
        for (let i = 0; i < dataLength; i++) {
          res.data.items[i].serial = (paging.page - 1) * paging.perPage + i + 1;

          await ServerApi.getStatistics(
            this.$http,
            res.data.items[i].fingerprint
          ).then((data) => {
            if (data) {
              res.data.items[i].statistics = data;
            }
          });
        }

        return res.data;
      });
    },

    // Show create and update page

    showCreatePage() {
      this.$router.push({
        name: "create-server-2",
      });
    },

    showUpdatePage(item) {
      this.$router.push({
        name: "update-server-2",
        params: { id: item.fingerprint },
      });
    },

    // Change status

    updateServer(sendingForm) {
      ServerApi.updateServer(this.$http, sendingForm).then((res) => {
        this.$store.dispatch("NOTIFY", {
          type: "success",
          text: res.message,
          duration: 10000,
        });
        this.$refs.table.loadItems();
      });
    },

    changeStatus(item) {
      const sendingForm = {
        _id: item._id,
        ip: item.ip,
        name: item.name,
        phone_number: item.phone_number,
        description: item.description,
        agentLimit: item.agentLimit,
        serverLimit: item.serverLimit,
        company: item.company,
        address: item.address,
        email: item.email,
        expiredAt: item.expiredAt,
        fingerprint: item.fingerprint,
        treeheight: item.treeheight,
        tags: item.tags,
        status: item.status ? false : true,
        lastConnection: item.lastConnection,
      };

      this.updateServer(sendingForm);
    },

    // Render table column
    renderTagName(vals) {
      let result = [];

      vals.map((item) => {
        const x = this.listTags.find((e) => e.id === item);
        if (x) {
          result.push(x.name);
        }
      });

      return result.join();
    },

    renderTypeName(val) {
      const x = this.listTypes.find((type) => type.value === val);
      if (!x) {
        return;
      }

      return x.name;
    },

    // Fetch

    fetchTags() {
      UpdateApi.getTagList(this.$http).then((data) => {
        let tags = data;
        this.listTags = tags.map((tag) => {
          return { name: tag.name, id: tag._id };
        });
      });
    },

    // History in server infor

    serverHistoryListFunc(
      filtering,
      sorting,
      paging,
      fingerprint = this.formModal.formInfor.values.fingerprint
    ) {
      return ServerApi.getServerHistoryVueTable(
        this.$http,
        filtering,
        sorting,
        paging,
        fingerprint
      ).then(function (res) {
        const dataLength = res.data.items.length;
        for (let i = 0; i < dataLength; i++) {
          res.data.items[i].serial = (paging.page - 1) * paging.perPage + i + 1;
        }

        return res.data;
      });
    },

    // Show server infor

    showServerInfor(item) {
      this.formModal.formInfor.values = item;
      this.formModal.formInfor.isFormOpen = true;
    },

    // Download server file

    downloadServerFile(item) {
      ServerApi.downloadServerFile(this.$http, item.fingerprint).then((res) => {
        this.$store.dispatch("NOTIFY", {
          type: "success",
          text: "Download successful",
          duration: 10000,
        });
      });
    },
  },

  created() {
    this.fetchTags();
  },
};
</script>

<style lang="scss" scoped>
@import "assets/app.scss";
.actions-column-server {
  display: flex;
  justify-content: center;

  .menu-button:hover {
    opacity: 0.5;
    cursor: pointer;
  }
}

.nowrap {
  white-space: nowrap;
}

.statistic {
  display: flex;
  justify-content: flex-start;
  border-radius: 4px;
  height: 86px;

  .count {
    margin-left: 20px;
    overflow: scroll;
    font-size: 30px;
    line-height: 70px;
    font-weight: bold;
    width: 100px;
  }

  .line {
    width: 2px;
    height: 70px;
    position: relative;
    top: 4px;
    margin-right: 20px;
  }

  .text {
    margin-right: 20px;
    font-size: 15px;
    line-height: 80px;
    overflow-x: auto;
    overflow-y: hidden;
  }
}

.close-icon {
  position: absolute;
  top: 15px;
  right: 15px;
  cursor: pointer;

  &:hover {
    opacity: 0.5;
  }
}

/deep/ button.modal-close {
  display: none;
}

.filter {
  z-index: 2;
  position: relative;
  cursor: pointer;
  font-weight: bold;
  left: 68.7%;
  top: 65px;
  width: 10px;
  .icon {
    .filter-active {
      color: #2096f2 !important;
    }
  }
}
</style>
